import { type DragEvent, type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';

import { SignaturePreview } from '@/features/signatures/SignaturesCard';
import { recipientsText, sendingState } from '@/shared/emails/sending';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input, Select } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { TechnicalDetails } from '@/shared/ui/TechnicalDetails';
import { RefreshIcon, SparkIcon, UsersIcon, XIcon } from '@/shared/ui/icons';
import { cn } from '@/shared/utils/cn';
import { formatBytes, formatDate } from '@/shared/utils/format';

import { type AiDraftChoice, AiDraftDialog } from './AiDraft';
import { AI_DRAFT_MODES, AI_REVIEW_MODES } from './aiDraftModes';
import {
  addressError,
  attachmentsProblem,
  COMPOSE_TITLES,
  groupsText,
  megabytes,
  newClientToken,
  outgoingFailureText,
  ownCopySentence,
  SENT_KIND,
  sentToText,
} from './outgoing';
import { AttachButton, OwnFilesList } from './OwnFiles';
import { RecipientField } from './RecipientField';
import {
  addressesOf,
  GROUP_HINT,
  groupsOf,
  MAX_GROUPS,
  MAX_TOTAL_RECIPIENTS,
  ONLY_GROUPS_HINT,
  recipientsCount,
  recipientsFrom,
  type RecipientsValue,
  TOO_MANY_RECIPIENTS,
} from './recipients';
import type {
  AiDraftMode,
  ComposeDefaults,
  ComposeSignature,
  Outgoing,
  OutgoingKind,
} from './types';
import {
  useComposeDefaults,
  useOutgoing,
  useOutgoingForEmail,
  useRetryOutgoingAppend,
  useSendOutgoing,
} from './useEmails';
import { useHoldStrayDrops, useOwnFiles } from './useOwnFiles';

/**
 * The signature under the text (contract v10.57): the one in HTML as it will arrive, or
 * the plain text one, with «Sin firma en este correo» and where to change it.
 */
function ComposeSignatureBlock({
  signature,
  signed,
  onSigned,
}: {
  signature: ComposeSignature;
  signed: boolean;
  onSigned: (on: boolean) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-ink-2 text-[13.5px] font-medium">Firma</span>
        <span className="flex items-center gap-3 text-[13px]">
          <label className="text-ink-2 flex items-center gap-1.5">
            <input
              type="checkbox"
              className="accent-accent size-4"
              checked={!signed}
              onChange={(event) => onSigned(!event.target.checked)}
            />
            Sin firma en este correo
          </label>
          <Link to="/perfil" className="text-accent hover:underline" target="_blank">
            Cambiarla
          </Link>
        </span>
      </div>
      {signed ? (
        signature.html ? (
          <SignaturePreview html={signature.html} className="max-h-72 overflow-y-auto" />
        ) : (
          <p className="border-line text-ink-2 rounded-md border p-3 text-[13.5px] whitespace-pre-wrap">
            {signature.text}
          </p>
        )
      ) : null}
    </div>
  );
}

const whenFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' });

/**
 * The mail manager's form (contract v10.40, ADR 0035 of the back): a reply, a reply to
 * all, a forward or a new message, sent from a Cubas mailbox with a copy left in its Sent
 * folder, as if it had been written in Thunderbird.
 *
 * Everything starts filled in by `GET /api/outgoing/defaults`, asked every time it opens:
 * the recipients, «Re:» or «Fwd:», the original quoted, and for a forward the attachments
 * of the original, ticked except the images of its body. Everything can be changed except
 * the mailbox of a reply or a forward, which is the one the original came in through.
 * When the back already knows it would refuse (`blocked_reason`: the lock is closed, the
 * mailbox has no outgoing server of its own), the sheet says so and does not offer
 * «Enviar».
 *
 * Contract v10.41: the body comes signed (the person's signature, from their profile),
 * and «Adjuntar», or dropping files on the sheet, uploads each file as soon as it is
 * chosen; «Enviar» waits for them and carries their ids. A back without v10.41 sends no
 * `max_attachment_bytes`, and then there is no «Adjuntar».
 *
 * Contract v10.44 (ADR 0038 of the back): the recipient fields are chips with suggestions
 * from the address book (`RecipientField`). A group picked in any of them travels in
 * `group_ids`, never as its addresses, and the back sends it as a blind copy to each
 * member; with a group, «Para» may stay empty and the back puts the mailbox there.
 */
export function ComposeDialog({
  kind,
  sourceEmailId,
  onClose,
  onSent,
}: {
  kind: OutgoingKind;
  sourceEmailId: string | null;
  onClose: () => void;
  onSent?: (outgoing: Outgoing) => void;
}) {
  const defaults = useComposeDefaults(kind, sourceEmailId);
  // Only while there is nothing to show: a refetch that fails in the background (the
  // network back, a deploy answering 502) must not unmount the form and lose what was
  // written.
  if (defaults.data === undefined) {
    return (
      <Dialog title={COMPOSE_TITLES[kind]} size="lg" onClose={onClose}>
        {defaults.isError ? (
          <ErrorMessage error={defaults.error}>No se pudo preparar el correo</ErrorMessage>
        ) : (
          <Spinner variant="block" label="Preparando el correo" />
        )}
      </Dialog>
    );
  }
  return (
    <ComposeForm
      kind={kind}
      sourceEmailId={sourceEmailId}
      defaults={defaults.data}
      onClose={onClose}
      onSent={onSent}
    />
  );
}

function ComposeForm({
  kind,
  sourceEmailId,
  defaults,
  onClose,
  onSent,
}: {
  kind: OutgoingKind;
  sourceEmailId: string | null;
  defaults: ComposeDefaults;
  onClose: () => void;
  onSent: ((outgoing: Outgoing) => void) | undefined;
}) {
  const send = useSendOutgoing();
  const [clientToken] = useState(newClientToken);
  const [to, setTo] = useState<RecipientsValue>(() => recipientsFrom(defaults.to));
  const [cc, setCc] = useState<RecipientsValue>(() => recipientsFrom(defaults.cc));
  const [bcc, setBcc] = useState<RecipientsValue>(() => recipientsFrom([]));
  const [subject, setSubject] = useState(defaults.subject ?? '');
  // Contract v10.57: a back that sends `quoted` sends the signature apart too, and then the
  // box holds only what the person writes. An older one sends it all in `body`.
  const apart = typeof defaults.quoted === 'string';
  const initialBody = apart ? '' : (defaults.body ?? '');
  const [body, setBody] = useState(initialBody);
  const [quoted, setQuoted] = useState(defaults.quoted ?? '');
  const [showQuoted, setShowQuoted] = useState(false);
  const [signed, setSigned] = useState(true);
  const [mailbox, setMailbox] = useState(defaults.mailbox ?? '');
  const [ticked, setTicked] = useState<Set<string>>(() => new Set(defaults.attachment_ids ?? []));
  const [tried, setTried] = useState(false);
  const [dragging, setDragging] = useState(false);
  // «Redactar con IA» (contract v10.56): the sheet open over this one, and what each
  // version picked replaced, newest last, for «Deshacer» and Ctrl+Z.
  const [aiMode, setAiMode] = useState<AiDraftMode | null>(null);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [aiUndo, setAiUndo] = useState<AiUndo[]>([]);
  const bodyField = useRef<HTMLTextAreaElement>(null);
  const [aiTaken, setAiTaken] = useState(0);
  const id = useId();
  const limit = defaults.max_attachment_bytes ?? 0;
  const canAttach = limit > 0;
  const ownFiles = useOwnFiles(limit);
  const own = ownFiles.files;

  const choices = defaults.mailbox_choices ?? [];
  const fromAddress =
    choices.find((choice) => choice.mailbox === mailbox)?.from_address ??
    defaults.from_address ??
    defaults.mailbox;
  const attachments = kind === 'forward' ? (defaults.attachments ?? []) : [];
  const signature = apart
    ? (defaults.signatures ?? []).find((each) => each.mailbox === mailbox)
    : undefined;

  const toList = addressesOf(to);
  const ccList = addressesOf(cc);
  const bccList = addressesOf(bcc);
  const groups = groupsOf(to, cc, bcc);
  // With a group, «Para» may be empty: the back puts the mailbox's own address there.
  const toError = addressError(toList, groups.length === 0);
  const ccError = addressError(ccList, false);
  const bccError = addressError(bccList, false);
  const groupsError =
    groups.length > MAX_GROUPS ? `Como mucho ${MAX_GROUPS} grupos por correo.` : undefined;
  // The back's ceiling counts the members of the groups too (contract v10.44): said here,
  // and not as a 422 after «Enviar».
  const totalError =
    recipientsCount(to, cc, bcc) > MAX_TOTAL_RECIPIENTS ? TOO_MANY_RECIPIENTS : undefined;
  const subjectError = subject.trim() ? undefined : 'Pon un asunto.';
  const mailboxError = mailbox ? undefined : 'Elige desde qué buzón sale.';
  const tickedAttachments = attachments.filter((attachment) => ticked.has(attachment.id));
  const filesProblem = attachmentsProblem(
    {
      count: tickedAttachments.length,
      bytes: tickedAttachments.reduce((sum, attachment) => sum + attachment.size_bytes, 0),
    },
    own,
    limit,
  );
  const { uploading, failed: failedFile } = ownFiles;
  const valid =
    !toError &&
    !ccError &&
    !bccError &&
    !groupsError &&
    !totalError &&
    !subjectError &&
    !mailboxError &&
    !filesProblem;
  const blocked = defaults.blocked_reason ?? null;
  const touched =
    toList.join() !== addressesOf(recipientsFrom(defaults.to)).join() ||
    ccList.join() !== addressesOf(recipientsFrom(defaults.cc)).join() ||
    bccList.length > 0 ||
    groups.length > 0 ||
    subject !== (defaults.subject ?? '') ||
    body !== initialBody ||
    quoted !== (defaults.quoted ?? '') ||
    mailbox !== (defaults.mailbox ?? '') ||
    own.length > 0 ||
    ticked.size !== (defaults.attachment_ids ?? []).length ||
    (defaults.attachment_ids ?? []).some((attachmentId) => !ticked.has(attachmentId));

  /** «Cancelar», the «×», Escape and a click outside: what was written is not thrown away
   * without asking, as Thunderbird asks before closing a message it has not sent. The
   * question is a sheet of the platform over this one, not the browser's own box, which
   * looks like another program and stops the whole tab until it is answered. */
  function requestClose(): void {
    if (send.isPending) return;
    if (touched) {
      setConfirmingClose(true);
      return;
    }
    onClose();
  }

  /** The version the person picked goes in; what was there before waits for «Deshacer». */
  function takeAiVersion(choice: AiDraftChoice): void {
    setAiUndo((current) => [...current, { body, subject, applied: choice.body }]);
    setBody(choice.body);
    // A subject only where there was none: one written by the person is theirs.
    if (choice.subject && !subject.trim()) setSubject(choice.subject);
    setAiMode(null);
    setAiTaken((count) => count + 1);
  }

  // Back to the message once the sheet is gone: closing, it gives the focus to the button
  // that opened it, and this runs after that.
  useEffect(() => {
    if (aiTaken > 0) bodyField.current?.focus();
  }, [aiTaken]);

  function undoAi(): void {
    const last = aiUndo[aiUndo.length - 1];
    if (!last) return;
    setAiUndo(aiUndo.slice(0, -1));
    setBody(last.body);
    setSubject(last.subject);
    bodyField.current?.focus();
  }

  // Something of the person's own above what came filled in: then there is something to
  // go over. The back draws the line exactly (at the signature) and says so if not.
  const wroteSomething = body.trim() !== initialBody.trim() && body.trim() !== '';
  const aiOff = send.isPending || (kind === 'new' && !mailbox);

  function toggle(attachmentId: string, on: boolean): void {
    setTicked((current) => {
      const next = new Set(current);
      if (on) next.add(attachmentId);
      else next.delete(attachmentId);
      return next;
    });
  }

  useHoldStrayDrops();

  function attach(list: FileList | null): void {
    // While it goes, a new file would not be in what was sent, and the sheet closes.
    if (!canAttach || !list || send.isPending) return;
    ownFiles.add(list);
  }

  function carriesFiles(event: DragEvent): boolean {
    return canAttach && Array.from(event.dataTransfer.types).includes('Files');
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    setTried(true);
    if (!valid || blocked || uploading) return;
    send.mutate(
      {
        kind,
        source_email_id: sourceEmailId,
        ...(kind === 'new' ? { mailbox } : {}),
        to: toList,
        cc: ccList,
        bcc: bccList,
        ...(groups.length > 0 ? { group_ids: groups.map((group) => group.id) } : {}),
        subject: subject.trim(),
        body,
        ...(apart
          ? {
              quoted,
              ...(signed && signature?.signature_id
                ? { signature_id: signature.signature_id }
                : {}),
              ...(signed && signature && !signature.signature_id ? { plain_signature: true } : {}),
            }
          : {}),
        // In the order of the original, whatever order they were ticked in.
        attachment_ids: tickedAttachments.map((attachment) => attachment.id),
        upload_ids: ownFiles.ids,
        client_token: clientToken,
      },
      {
        onSuccess: (outgoing) => {
          onSent?.(outgoing);
          onClose();
        },
      },
    );
  }

  return (
    <>
      <Dialog
        title={COMPOSE_TITLES[kind]}
        size="lg"
        onClose={requestClose}
        footer={
          <>
            <Button variant="secondary" onClick={requestClose} disabled={send.isPending}>
              Cancelar
            </Button>
            <Button
              type="submit"
              form={`${id}-form`}
              disabled={send.isPending || !!blocked || uploading}
            >
              {send.isPending ? 'Enviando…' : uploading ? 'Subiendo adjuntos…' : 'Enviar'}
            </Button>
          </>
        }
      >
        <form
          id={`${id}-form`}
          className={cn(
            'flex flex-col gap-3 rounded-md',
            dragging && 'outline-accent outline-2 outline-offset-4 outline-dashed',
          )}
          onSubmit={submit}
          noValidate
          onDragOver={(event) => {
            if (!carriesFiles(event)) return;
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null))
              setDragging(false);
          }}
          onDrop={(event) => {
            if (!carriesFiles(event)) return;
            event.preventDefault();
            setDragging(false);
            attach(event.dataTransfer.files);
          }}
        >
          {blocked ? (
            <Callout tone="warn" title="Desde aquí no se puede enviar todavía">
              {blocked}
            </Callout>
          ) : null}
          {kind === 'new' && choices.length > 1 ? (
            <Select
              label="Desde"
              value={mailbox}
              onChange={(event) => setMailbox(event.target.value)}
              hint="Queda una copia en su carpeta de Enviados, como si lo mandaras desde Thunderbird."
              error={tried ? mailboxError : undefined}
            >
              <option value="">Elige un buzón…</option>
              {choices.map((choice) => (
                <option key={choice.mailbox} value={choice.mailbox}>
                  {choice.from_address}
                </option>
              ))}
            </Select>
          ) : fromAddress ? (
            <p className="text-ink-2 text-[13.5px]">
              Desde <strong className="text-ink">{fromAddress}</strong>. Queda una copia en su
              carpeta de Enviados, como si lo mandaras desde Thunderbird.
            </p>
          ) : null}
          <RecipientField
            label="Para"
            value={to}
            onChange={setTo}
            mailbox={mailbox}
            autoFocus={!(kind === 'new' && choices.length > 1)}
            hint={
              groups.length > 0 && toList.length === 0
                ? ONLY_GROUPS_HINT
                : 'Escribe un nombre, una dirección o un grupo. Varias, separadas por comas.'
            }
            error={tried ? toError : undefined}
          />
          <RecipientField
            label="Copia"
            optional
            value={cc}
            onChange={setCc}
            mailbox={mailbox}
            error={tried ? ccError : undefined}
          />
          <RecipientField
            label="Copia oculta"
            optional
            value={bcc}
            onChange={setBcc}
            mailbox={mailbox}
            hint="Los demás destinatarios no ven estas direcciones."
            error={tried ? bccError : undefined}
          />
          {groups.length > 0 ? (
            <p className="text-ink-2 flex items-start gap-2 text-[13px] leading-snug">
              <UsersIcon size={15} className="text-ink-3 mt-0.5 shrink-0" />
              <span>{GROUP_HINT}</span>
            </p>
          ) : null}
          {tried && groupsError ? (
            <p role="alert" className="text-danger text-[13px]">
              {groupsError}
            </p>
          ) : null}
          {tried && totalError ? (
            <p role="alert" className="text-danger text-[13px]">
              {totalError}
            </p>
          ) : null}
          <Input
            label="Asunto"
            value={subject}
            maxLength={900}
            onChange={(event) => setSubject(event.target.value)}
            error={tried ? subjectError : undefined}
          />
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <label htmlFor={`${id}-body`} className="text-ink-2 text-[13.5px] font-medium">
                Mensaje
              </label>
              <Button
                variant="ai-soft"
                size="sm"
                icon={<SparkIcon size={14} />}
                onClick={() => setAiMode('write')}
                disabled={aiOff}
                title={kind === 'new' && !mailbox ? 'Elige antes desde qué buzón sale.' : undefined}
              >
                {AI_DRAFT_MODES.write}
              </Button>
            </div>
            <textarea
              ref={bodyField}
              id={`${id}-body`}
              className="field h-auto min-h-56 py-2"
              rows={14}
              maxLength={200_000}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              onKeyDown={(event) => {
                // Ctrl+Z right after picking the AI's version brings back what was there: the
                // browser's own undo does not know the text changed. Once the person types,
                // the browser's undo is theirs again.
                const last = aiUndo[aiUndo.length - 1];
                if (
                  last &&
                  body === last.applied &&
                  (event.ctrlKey || event.metaKey) &&
                  !event.shiftKey &&
                  event.key.toLowerCase() === 'z'
                ) {
                  event.preventDefault();
                  undoAi();
                }
              }}
            />
            {wroteSomething || aiUndo.length > 0 ? (
              <div
                role="group"
                aria-label="Repasar con IA"
                className="flex flex-wrap items-center gap-x-1.5 gap-y-1"
              >
                {wroteSomething ? (
                  <>
                    <span className="text-ai inline-flex items-center gap-1 text-[12.5px] font-medium">
                      <SparkIcon size={12} />
                      Repasar con IA:
                    </span>
                    {AI_REVIEW_MODES.map((mode) => (
                      <Button
                        key={mode}
                        variant="ai-soft"
                        size="sm"
                        onClick={() => setAiMode(mode)}
                        disabled={aiOff}
                      >
                        {AI_DRAFT_MODES[mode]}
                      </Button>
                    ))}
                  </>
                ) : null}
                {aiUndo.length > 0 ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<RefreshIcon size={14} />}
                    onClick={undoAi}
                    disabled={send.isPending}
                    className="ml-auto"
                  >
                    Deshacer
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
          {signature ? (
            <ComposeSignatureBlock signature={signature} signed={signed} onSigned={setSigned} />
          ) : null}
          {apart && quoted ? (
            <div className="flex flex-col gap-1">
              <button
                type="button"
                className="text-accent self-start text-[13.5px] font-medium hover:underline"
                aria-expanded={showQuoted}
                onClick={() => setShowQuoted(!showQuoted)}
              >
                {showQuoted
                  ? 'Ocultar el mensaje de debajo'
                  : kind === 'forward'
                    ? 'Ver el mensaje reenviado'
                    : 'Ver el mensaje citado'}
              </button>
              {showQuoted ? (
                <textarea
                  aria-label={kind === 'forward' ? 'Mensaje reenviado' : 'Mensaje citado'}
                  className="field text-ink-2 h-auto min-h-40 py-2 text-[13.5px]"
                  rows={10}
                  maxLength={200_000}
                  value={quoted}
                  onChange={(event) => setQuoted(event.target.value)}
                />
              ) : null}
            </div>
          ) : null}
          {attachments.length > 0 ? (
            <fieldset className="flex flex-col gap-1.5">
              <legend className="text-ink-2 mb-1 text-[13.5px] font-medium">Adjuntos</legend>
              {attachments.map((attachment) => (
                <label
                  key={attachment.id}
                  className="text-ink-2 flex flex-wrap items-center gap-2 text-[14px]"
                >
                  <input
                    type="checkbox"
                    className="accent-accent size-4"
                    checked={ticked.has(attachment.id)}
                    onChange={(event) => toggle(attachment.id, event.target.checked)}
                  />
                  <span className="min-w-0 break-all">{attachment.filename}</span>
                  <span className="text-ink-4 text-[12.5px]">
                    {formatBytes(attachment.size_bytes)}
                    {attachment.inline ? ' · imagen del cuerpo' : ''}
                  </span>
                </label>
              ))}
            </fieldset>
          ) : null}
          {canAttach ? (
            <div className="flex flex-col gap-1.5">
              <AttachButton
                onFiles={attach}
                disabled={send.isPending}
                hint={`O arrastra aquí los ficheros. Hasta ${megabytes(limit)} entre todos.`}
              />
              <OwnFilesList files={own} onRemove={ownFiles.remove} disabled={send.isPending} />
              {filesProblem && (tried || !failedFile) ? (
                <p role="alert" className="text-danger text-[13px]">
                  {filesProblem}
                </p>
              ) : null}
            </div>
          ) : null}
          {send.isError ? <ErrorMessage error={send.error}>No se pudo enviar</ErrorMessage> : null}
        </form>
      </Dialog>
      {aiMode ? (
        // Beside the form and not inside it: its own buttons must never submit «Enviar».
        <AiDraftDialog
          mode={aiMode}
          kind={kind}
          sourceEmailId={sourceEmailId}
          mailbox={mailbox}
          subject={subject}
          body={body}
          signed={signed && !!signature}
          onUse={takeAiVersion}
          onClose={() => setAiMode(null)}
        />
      ) : null}
      {confirmingClose ? (
        <Dialog
          title="¿Cerrar sin enviar?"
          size="sm"
          onClose={() => setConfirmingClose(false)}
          footer={
            <>
              <Button variant="secondary" data-autofocus onClick={() => setConfirmingClose(false)}>
                Seguir escribiendo
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setConfirmingClose(false);
                  onClose();
                }}
              >
                Cerrar y perder lo escrito
              </Button>
            </>
          }
        >
          <p className="text-ink-2 text-[14px] leading-relaxed">
            El correo no se ha enviado. Si lo cierras ahora, se pierde lo que has escrito.
          </p>
        </Dialog>
      ) : null}
    </>
  );
}

/** What a version picked from «Redactar con IA» replaced, and what it put. */
interface AiUndo {
  body: string;
  subject: string;
  applied: string;
}

/**
 * «Respuestas y reenvíos»: what was sent from this email, newest first, where each one
 * stands and its copy in Sent. Nothing at all while nothing was sent.
 */
export function SentFromHere({ emailId, enabled }: { emailId: string; enabled: boolean }) {
  const sent = useOutgoingForEmail(emailId, enabled);
  const retry = useRetryOutgoingAppend();
  const items = sent.data?.items ?? [];
  if (items.length === 0) return null;
  return (
    <section className="flex flex-col gap-2" aria-label="Respuestas y reenvíos">
      <h2 className="text-ink-2 text-[13.5px] font-medium">Respuestas y reenvíos</h2>
      <ul className="flex flex-col gap-2">
        {items.map((outgoing) => (
          <OutgoingLine
            key={outgoing.id}
            outgoing={outgoing}
            onRetry={() => retry.mutate(outgoing.id)}
            retrying={retry.isPending && retry.variables === outgoing.id}
          />
        ))}
      </ul>
      {retry.isError ? (
        <ErrorMessage error={retry.error}>No se pudo volver a guardar la copia</ErrorMessage>
      ) : null}
    </section>
  );
}

/**
 * The line the inbox keeps after «Redactar»: the message as it goes, until it is sent
 * and its copy is in Sent, or the reason it was not. «×» hides it.
 */
export function JustSent({ id, onDismiss }: { id: string; onDismiss: () => void }) {
  const outgoing = useOutgoing(id);
  const retry = useRetryOutgoingAppend();
  if (!outgoing.data) return null;
  return (
    <div className="flex items-start gap-2">
      <ul className="min-w-0 flex-1">
        <OutgoingLine
          outgoing={outgoing.data}
          onRetry={() => retry.mutate(id)}
          retrying={retry.isPending}
        />
      </ul>
      <Button
        variant="ghost"
        size="sm"
        icon={<XIcon size={14} />}
        onClick={onDismiss}
        aria-label="Ocultar"
      >
        <span className="sr-only">Ocultar</span>
      </Button>
    </div>
  );
}

/**
 * What its owner sent from the mail client, read from the Sent folder (contract v10.43,
 * ADR 0037 of the back): already sent, with no author and nothing to retry. Only the
 * names of its files: the files themselves stay in the mailbox.
 */
function FromMailClientLine({ outgoing }: { outgoing: Outgoing }) {
  const cc = outgoing.cc_addresses ?? [];
  const names = outgoing.attachment_names ?? [];
  return (
    <li className="bg-paper-2 text-ink-2 flex flex-col gap-1 rounded-md px-3 py-2 text-[13.5px] [overflow-wrap:anywhere]">
      <span>
        {SENT_KIND[outgoing.kind]} a {recipientsText(outgoing.to_addresses)}
        {cc.length > 0 ? ` con copia a ${recipientsText(cc)}` : ''}
        {outgoing.sent_at ? ` · ${formatDate(whenFormat, outgoing.sent_at)}` : ''}
      </span>
      <span className="text-ink-3 text-[12.5px]">
        Enviado desde el programa de correo
        {names.length > 0 ? ` · ${names.join(', ')}` : ''}
      </span>
    </li>
  );
}

function OutgoingLine({
  outgoing,
  onRetry,
  retrying,
}: {
  outgoing: Outgoing;
  onRetry: () => void;
  retrying: boolean;
}) {
  const state = sendingState(outgoing);
  // The groups it was written to (contract v10.44): their members are in the blind copy.
  const groups = outgoing.group_names ?? [];
  const to = sentToText(outgoing);
  const what = SENT_KIND[outgoing.kind];
  if (outgoing.origin === 'mail_client') return <FromMailClientLine outgoing={outgoing} />;
  if (state.kind === 'sending') {
    return (
      <li
        role="status"
        className="bg-paper-2 text-ink-2 rounded-md px-3 py-2 text-[13.5px] [overflow-wrap:anywhere]"
      >
        Enviando {to}… esta pantalla se actualiza sola.
      </li>
    );
  }
  if (state.kind === 'failed') {
    return (
      <li
        role="alert"
        className="bg-danger-soft text-danger rounded-md px-3 py-2 text-[13.5px] [overflow-wrap:anywhere]"
      >
        <span className="font-medium">No se envió {to}</span>
        {outgoingFailureText(state.reason)}
        <TechnicalDetails text={state.reason} />
      </li>
    );
  }
  const cc = outgoing.cc_addresses ?? [];
  const bcc = outgoing.bcc_addresses ?? [];
  const copyText = ownCopySentence(state.copyError);
  return (
    <li className="bg-ok-soft text-ok flex flex-col gap-1 rounded-md px-3 py-2 text-[13.5px] [overflow-wrap:anywhere]">
      <span>
        {what} {sentToText(outgoing, { groups: false })}
        {cc.length > 0 ? ` con copia a ${recipientsText(cc)}` : ''}
        {bcc.length > 0
          ? `${cc.length > 0 ? ' y' : ' con'} copia oculta a ${recipientsText(bcc)}`
          : ''}
        {outgoing.sent_at ? ` · ${formatDate(whenFormat, outgoing.sent_at)}` : ''}
        {outgoing.created_by_name ? ` · ${outgoing.created_by_name}` : ''}
      </span>
      {groups.length > 0 ? (
        <span className="text-ink-2 inline-flex items-start gap-1.5 text-[12.5px]">
          <UsersIcon size={13} className="mt-0.5 shrink-0" />
          <span>
            {groupsText(groups, { capital: true })}, en copia oculta: nadie vio las direcciones de
            los demás.
          </span>
        </span>
      ) : null}
      {outgoing.error ? (
        // Sent, but the server refused some of the recipients while it took the others.
        <span role="alert" className="text-warn text-[12.5px]">
          {outgoing.error}
        </span>
      ) : null}
      {state.copy === 'saved' ? (
        <span className="text-ink-3 text-[12.5px]">
          Copia en Enviados de {outgoing.from_address ?? outgoing.mailbox}.
        </span>
      ) : state.copy === 'saving' ? (
        <span className="text-ink-3 text-[12.5px]">Guardando la copia en Enviados…</span>
      ) : (
        <span className="text-warn flex flex-col items-start gap-1 text-[12.5px]">
          {copyText ??
            (state.copy === 'failed'
              ? 'Se envió, pero no se guardó la copia en Enviados de tu correo.'
              : 'No consta la copia en Enviados de tu correo.')}
          {state.copyError && !copyText ? <TechnicalDetails text={state.copyError} /> : null}
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshIcon size={14} />}
            onClick={onRetry}
            disabled={retrying}
          >
            {retrying ? 'Guardando…' : 'Reintentar la copia'}
          </Button>
        </span>
      )}
    </li>
  );
}
